/**
 * AchChart — Dynamic ACH % chart with BDM / Brand / State grouping.
 *
 * Compact view : top 5, horizontal bar.
 * Expanded view: full dataset, switchable chart type + all toolbar controls.
 */
import { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  AreaChart, Area,
  XAxis, YAxis,
  CartesianGrid, Tooltip,
  Cell, ReferenceLine,
  PieChart, Pie,
} from 'recharts';
import ChartContainer from './ChartContainer';
import ChartModal from '../ChartModal/ChartModal';
import useChartExpand from '../../hooks/useChartExpand';
import './AchChart.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
const calcAch = (cy, target) =>
  target > 0 ? +((cy / target) * 100).toFixed(1) : 0;

const getColor = (pct) => {
  if (pct >= 100) return '#16a34a';
  if (pct >= 90)  return '#2563eb';
  if (pct >= 80)  return '#ea580c';
  return '#dc2626';
};

// Rich distinct palette for pie slices (cycles if more items than colours)
const PIE_PALETTE = [
  '#C81E1E','#2563eb','#16a34a','#ea580c','#7c3aed',
  '#0891b2','#d97706','#db2777','#059669','#9333ea',
  '#0284c7','#65a30d','#e11d48','#0d9488','#b45309',
];

const fmt = (v) => `₹${(Number(v ?? 0) / 100000).toFixed(2)}L`;

// ── Bar/Line/Area tooltip ─────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d   = payload[0]?.payload;
  const pct = d?.ach_pct ?? 0;
  return (
    <div className="ach-tooltip">
      <p className="ach-tooltip__title">{label}</p>
      <p className="ach-tooltip__row">Ach %: <strong style={{ color: getColor(pct) }}>{pct}%</strong></p>
      <p className="ach-tooltip__row">CY Sales: <strong>{fmt(d?.cy_sales)}</strong></p>
      <p className="ach-tooltip__row">Target: <strong>{fmt(d?.target)}</strong></p>
    </div>
  );
};

// ── Pie tooltip ───────────────────────────────────────────────────────────────
const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d   = payload[0]?.payload;
  const pct = d?.ach_pct ?? 0;
  const sharePct = d?._sharePct ?? 0;
  return (
    <div className="ach-tooltip">
      <p className="ach-tooltip__title">{d?.label}</p>
      <p className="ach-tooltip__row">Ach %: <strong style={{ color: getColor(pct) }}>{pct}%</strong></p>
      <p className="ach-tooltip__row">CY Sales: <strong>{fmt(d?.cy_sales)}</strong></p>
      <p className="ach-tooltip__row">Target: <strong>{fmt(d?.target)}</strong></p>
      <p className="ach-tooltip__row">Sales Share: <strong>{sharePct}%</strong></p>
    </div>
  );
};

// ── Build full dataset ────────────────────────────────────────────────────────
const buildData = (view, bdmData, brandData, stateData, hqData) => {
  switch (view) {
    case 'bdm':
      return (bdmData ?? []).map(r => ({
        label: r.bdm, cy_sales: r.cy_sales, target: r.target,
        ach_pct: r.ach_pct ?? calcAch(r.cy_sales, r.target),
      }));
    case 'brand':
      return (brandData ?? []).map(r => ({
        label: r.brand_name, cy_sales: r.cy_sales, target: r.target,
        ach_pct: calcAch(r.cy_sales, r.target),
      }));
    case 'state':
      return (stateData ?? []).map(r => ({
        label: r.state, cy_sales: r.cy_sales,
        target: r.target_value ?? r.target,
        ach_pct: calcAch(r.cy_sales, r.target_value ?? r.target),
      }));
    case 'hq':
      return (hqData ?? []).map(r => ({
        label: r.hq_name, cy_sales: r.cy_sales, target: r.target,
        ach_pct: r.ach_pct ?? calcAch(r.cy_sales, r.target),
      }));
    default: return [];
  }
};

// ── Attractive Pie / Donut view ───────────────────────────────────────────────
const PieView = ({ data }) => {
  const [activeIdx, setActiveIdx] = useState(null);

  // Enrich with share % and palette colour
  const totalSales = data.reduce((s, r) => s + (r.cy_sales ?? 0), 0);
  const enriched   = data.map((r, i) => ({
    ...r,
    _color:    PIE_PALETTE[i % PIE_PALETTE.length],
    _sharePct: totalSales > 0 ? +((r.cy_sales / totalSales) * 100).toFixed(1) : 0,
  }));

  // Centre label: show hovered item or overall avg ach
  const avgAch = data.length
    ? (data.reduce((s, r) => s + r.ach_pct, 0) / data.length).toFixed(1)
    : 0;
  const active = activeIdx != null ? enriched[activeIdx] : null;

  return (
    <div className="pie-view">
      {/* ── Donut + centre label ── */}
      <div className="pie-view__donut-wrap">
        <ResponsiveContainer width="100%" height={380}>
          <PieChart>
            <Pie
              data={enriched}
              dataKey="cy_sales"
              nameKey="label"
              cx="50%" cy="50%"
              outerRadius={150}
              innerRadius={82}
              paddingAngle={2}
              strokeWidth={0}
              onMouseEnter={(_, idx) => setActiveIdx(idx)}
              onMouseLeave={() => setActiveIdx(null)}
            >
              {enriched.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry._color}
                  opacity={activeIdx == null || activeIdx === i ? 1 : 0.35}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                />
              ))}
            </Pie>
            <Tooltip content={<PieTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* SVG-overlay centre text */}
        <div className="pie-view__centre">
          {active ? (
            <>
              <span className="pie-view__centre-label">{active.label}</span>
              <span className="pie-view__centre-value" style={{ color: getColor(active.ach_pct) }}>
                {active.ach_pct}%
              </span>
              <span className="pie-view__centre-sub">Ach %</span>
            </>
          ) : (
            <>
              <span className="pie-view__centre-label">Avg Ach</span>
              <span className="pie-view__centre-value" style={{ color: getColor(Number(avgAch)) }}>
                {avgAch}%
              </span>
              <span className="pie-view__centre-sub">{data.length} items</span>
            </>
          )}
        </div>
      </div>

      {/* ── Legend grid ── */}
      <div className="pie-view__legend">
        {enriched.map((entry, i) => (
          <div
            key={i}
            className={`pie-view__legend-item ${activeIdx === i ? 'pie-view__legend-item--active' : ''}`}
            onMouseEnter={() => setActiveIdx(i)}
            onMouseLeave={() => setActiveIdx(null)}
          >
            <span className="pie-view__legend-dot" style={{ background: entry._color }} />
            <span className="pie-view__legend-name">{entry.label}</span>
            <span className="pie-view__legend-ach" style={{ color: getColor(entry.ach_pct) }}>
              {entry.ach_pct}%
            </span>
            <span className="pie-view__legend-sales">{fmt(entry.cy_sales)}</span>
            <span className="pie-view__legend-share">{entry._sharePct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Custom Y-axis tick — highlights (VACANT) labels in red ────────────────────
const VacantAwareTick = ({ x, y, payload }) => {
  const label = payload?.value ?? '';
  const isVacant = label.toUpperCase().includes('(VACANT)');
  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fontSize={11}
      fontWeight={isVacant ? 700 : 600}
      fill={isVacant ? '#dc2626' : '#0f172a'}
    >
      {label}
    </text>
  );
};

// ── Custom X-axis tick — highlights (VACANT) labels in red ────────────────────
const VacantAwareXTick = ({ x, y, payload }) => {
  const label = payload?.value ?? '';
  const isVacant = label.toUpperCase().includes('(VACANT)');
  return (
    <text
      x={x}
      y={y}
      dy={12}
      textAnchor="middle"
      fontSize={11}
      fontWeight={isVacant ? 700 : 400}
      fill={isVacant ? '#dc2626' : '#64748b'}
    >
      {label}
    </text>
  );
};

// ── Shared chart renderer ─────────────────────────────────────────────────────
const AchChartInner = ({ data, chartType = 'hbar', height }) => {
  const h          = height ?? Math.max(200, data.length * 44);
  const tickStyle  = { fontSize: 11, fill: '#64748b' };

  if (chartType === 'pie') return <PieView data={data} />;

  return (
    <ChartContainer height={h}>
      {(w, ch) => {
        if (chartType === 'line') {
          return (
            <LineChart width={w} height={ch} data={data} margin={{ top: 10, right: 24, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="label" tick={<VacantAwareXTick />} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `${v}%`} tick={tickStyle} axisLine={false} tickLine={false} domain={[0, 'auto']} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={100} stroke="#16a34a" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="ach_pct" stroke="#C81E1E" strokeWidth={2.5}
                dot={(props) => {
                  const { cx, cy: cyPos, payload } = props;
                  return <circle key={`dot-${cx}-${cyPos}`} cx={cx} cy={cyPos} r={4} fill={getColor(payload.ach_pct)} stroke="#fff" strokeWidth={1.5} />;
                }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          );
        }
        if (chartType === 'area') {
          return (
            <AreaChart width={w} height={ch} data={data} margin={{ top: 10, right: 24, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="label" tick={<VacantAwareXTick />} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `${v}%`} tick={tickStyle} axisLine={false} tickLine={false} domain={[0, 'auto']} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={100} stroke="#16a34a" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="ach_pct" stroke="#C81E1E" fill="#C81E1E" fillOpacity={0.12} strokeWidth={2.5} />
            </AreaChart>
          );
        }
        if (chartType === 'bar') {
          return (
            <BarChart width={w} height={ch} data={data} margin={{ top: 10, right: 48, left: 8, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
              <XAxis dataKey="label" tick={<VacantAwareXTick />} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `${v}%`} tick={tickStyle} axisLine={false} tickLine={false} domain={[0, 'auto']} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
              <ReferenceLine y={100} stroke="#16a34a" strokeDasharray="4 4" />
              <Bar dataKey="ach_pct" radius={[3,3,0,0]} maxBarSize={28}
                label={{ position: 'top', formatter: v => `${v}%`, fontSize: 10, fill: '#64748b' }}>
                {data.map((entry, i) => <Cell key={i} fill={getColor(entry.ach_pct)} />)}
              </Bar>
            </BarChart>
          );
        }
        // default: hbar
        return (
          <BarChart width={w} height={ch} data={data} layout="vertical" margin={{ top: 5, right: 48, left: 8, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
            <XAxis type="number" tickFormatter={v => `${v}%`} tick={tickStyle} axisLine={false} tickLine={false} domain={[0, 'auto']} />
            <YAxis type="category" dataKey="label" tick={<VacantAwareTick />} axisLine={false} tickLine={false} width={130} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
            <ReferenceLine x={100} stroke="#16a34a" strokeDasharray="4 4" />
            <Bar dataKey="ach_pct" radius={[0,4,4,0]} maxBarSize={20}
              label={{ position: 'right', formatter: v => `${v}%`, fontSize: 11, fill: '#64748b' }}>
              {data.map((entry, i) => <Cell key={i} fill={getColor(entry.ach_pct)} />)}
            </Bar>
          </BarChart>
        );
      }}
    </ChartContainer>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const AchChart = ({ monthlyData, bdmData, brandData, stateData, hqData, loading, view = 'bdm' }) => {
  const { isOpen, open, close } = useChartExpand();

  const fullData = useMemo(
    () => buildData(view, bdmData, brandData, stateData, hqData).sort((a, b) => b.ach_pct - a.ach_pct),
    [view, bdmData, brandData, stateData, hqData]
  );

  const compactData = useMemo(() => fullData.slice(0, 5), [fullData]);

  const stats = useMemo(() => {
    if (!fullData.length) return [];
    const avg  = (fullData.reduce((s, r) => s + r.ach_pct, 0) / fullData.length).toFixed(1);
    const hits = fullData.filter(r => r.ach_pct >= 100).length;
    return [
      { label: 'Total Records', value: fullData.length },
      { label: 'Avg Ach %',     value: `${avg}%`,  color: getColor(Number(avg)) },
      { label: '≥100% Count',   value: hits,        color: '#16a34a' },
      { label: '<80% Count',    value: fullData.filter(r => r.ach_pct < 80).length, color: '#dc2626' },
    ];
  }, [fullData]);

  const viewLabel = view.charAt(0).toUpperCase() + view.slice(1);

  if (loading) return <div className="ach-chart"><div className="ach-chart__skeleton" /></div>;
  if (!compactData.length) return <div className="ach-chart"><div className="ach-chart__empty">No Data Available</div></div>;

  return (
    <>
      <div className="ach-chart">
        <div className="ach-chart__compact-header">
          <span className="ach-chart__compact-label">Top 5 by {viewLabel}</span>
          <button className="expand-btn" onClick={open} title="Expand chart">⤢</button>
        </div>
        <div className="ach-chart__body">
          <AchChartInner data={compactData} chartType="hbar" />
        </div>
        <div className="ach-chart__legend">
          <span className="ach-legend--green">≥100%</span>
          <span className="ach-legend--blue">≥90%</span>
          <span className="ach-legend--orange">≥80%</span>
          <span className="ach-legend--red">&lt;80%</span>
        </div>
      </div>

      <ChartModal
        open={isOpen}
        onClose={close}
        title={`ACH % — ${viewLabel}`}
        data={fullData}
        labelKey="label"
        valueKey="ach_pct"
        supportedTypes={['hbar', 'bar', 'line', 'area', 'pie']}
        defaultType="hbar"
        stats={stats}
        exportFilename={`ach-${view}`}
      >
        {(chartType, processedData) => (
          <AchChartInner
            data={processedData}
            chartType={chartType}
            height={chartType === 'pie' ? undefined : Math.max(360, processedData.length * (chartType === 'hbar' ? 40 : 32))}
          />
        )}
      </ChartModal>
    </>
  );
};

export default AchChart;
